import React from "react";
import PropTypes from 'prop-types';

import ToastContext from "./ToastContext";
import Toast from '../Toast/Toast';

interface ToastProviderProps {
    children: React.ReactNode;
}

interface ToastProviderItem {
    message: string,
    type: 'success' | 'informational' | 'error' | 'warning',
    onClose: () => void,
}

interface ToastProviderState {
    toasts: ToastProviderItem[],
    hideLead: boolean,
}

/**
 * A wrapper for an application to allow creating toasts via a command.
 * 
 * ## _
 * ### Arguments
 * @param {React.ReactNode} children - Application to be wrapped in toastiness.
 * 
 * ## _
 * ### Functions
 * @param {function} createToast - Creates a toast. Needs a message, a type, and an optional onClose function call.
 * @param {function} createSuccessToast - Creates a success toast. Needs a message and an optional onClose function call.
 * @param {function} createInfoToast - Creates an informational toast. Needs a message and an optional onClose function call.
 * @param {function} createWarningToast - Creates a warning toast. Needs a message and an optional onClose function call.
 * @param {function} createErrorToast - Creates an error toast. Needs a message and an optional onClose function call.
 */
class ToastProvider extends React.Component<ToastProviderProps, ToastProviderState> {
    static propTypes = {
        children: PropTypes.node.isRequired,
    }

    // toasts - list of toasts to be shown. Will be shown in list order.
    // hideLead - some jank to make sure the animations play. Will toggle visibility for the toast currently being shown.
    state: ToastProviderState = {
        toasts: [],
        hideLead: false,
    };

    createToast = (message: string, type: ('success' | 'informational' | 'error' | 'warning'), onClose: () => void = () => {}) => {
        // Append the toast to the "queue" of toasts.
        this.setState((prevState) => ({
            toasts: [...prevState.toasts, {message, type, onClose}],
        }));
    }

    createSuccessToast = (message: string, onClose: () => void) => {
        return this.createToast(message, "success", onClose);
    }

    createInfoToast = (message: string, onClose: () => void) => {
        return this.createToast(message, "informational", onClose);
    }

    createWarningToast = (message: string, onClose: () => void) => {
        return this.createToast(message, "warning", onClose);
    }

    createErrorToast = (message: string, onClose: () => void) => {
        return this.createToast(message, "error", onClose);
    }

    clearToasts = () => {
        this.setState(() => ({
            toasts: [],
            hideLead: false,
        }))
    }

    removeToast = () => {
        this.setState((prevState) => ({
            toasts: prevState.toasts.slice(1),
            hideLead: false,
        }))
    }

    render() {
        const { children } = this.props;

        return (
            <ToastContext.Provider value={{ 
                clearToasts: this.clearToasts,
                createToast: this.createToast,
                createSuccessToast: this.createSuccessToast, 
                createWarningToast: this.createWarningToast,
                createErrorToast: this.createErrorToast,
                createInfoToast: this.createInfoToast,
            }}>
                {children}
                {this.state.toasts.map((toast, index) => (
                    // Create every toast. Only make the first one visible. As the toasts leave the queue, the next will be shown.
                    <Toast 
                        key={`fusion-toast-${index}`}
                        isHidden={index != 0 || this.state.hideLead}
                        type={toast.type}
                        onClose={() => {
                            // This is on a timeout to allow the animation of the toast disappearing to play.
                            // Once the animation is complete, the toast is removed via the removeToast function.
                            this.setState({hideLead: true});
                            toast.onClose();
                            setTimeout(() => this.removeToast(), 500);
                        }}
                        message={toast.message}
                    />
                ))}
            </ToastContext.Provider>
        );
    };
}

export default ToastProvider;